/**
 * グラフ用の集計。日別 / 週別 / 月別で同じ形の配列 (Bucket[]) に畳んでから描く。
 *
 * トレーニング重量は「合計」、体重・体脂肪率は「平均」で集める。
 * 重量は足し算に意味があるが、体組成を足すと意味を失うため。
 */
import { monthKeyOf, mondayOf, shiftDays, shiftMonthKey, today, toKey, parseKey } from './dates';
import { type Workout, volumeOf } from './types';

export type Grain = 'day' | 'week' | 'month';

export const GRAINS: { id: Grain; label: string; buckets: number }[] = [
  { id: 'day', label: '日別', buckets: 30 },
  { id: 'week', label: '週別', buckets: 12 },
  { id: 'month', label: '月別', buckets: 12 },
];

export type Bucket = {
  key: string;
  /** 軸に出す短いラベル */
  label: string;
  /** ツールチップに出す説明 */
  caption: string;
  volumeKg: number;
  bodyWeight: number | null;
  bodyFat: number | null;
  trainedDays: number;
};

const bucketCount = (grain: Grain) => GRAINS.find((g) => g.id === grain)!.buckets;

/** その粒度で必要になる取得開始日 */
export function rangeFor(grain: Grain, to = today()): { from: string; to: string } {
  const n = bucketCount(grain);
  if (grain === 'day') return { from: shiftDays(to, -(n - 1)), to };
  if (grain === 'week') return { from: shiftDays(mondayOf(to), -7 * (n - 1)), to };
  return { from: `${shiftMonthKey(monthKeyOf(to), -(n - 1))}-01`, to };
}

const keyOf = (grain: Grain, date: string) =>
  grain === 'day' ? date : grain === 'week' ? mondayOf(date) : monthKeyOf(date);

/** 空のバケットを、古い順に並べて作る */
function emptyBuckets(grain: Grain, to: string): Bucket[] {
  const n = bucketCount(grain);
  const keys: string[] = [];

  if (grain === 'day') {
    for (let i = n - 1; i >= 0; i--) keys.push(shiftDays(to, -i));
  } else if (grain === 'week') {
    const thisMonday = mondayOf(to);
    for (let i = n - 1; i >= 0; i--) keys.push(shiftDays(thisMonday, -7 * i));
  } else {
    const thisMonth = monthKeyOf(to);
    for (let i = n - 1; i >= 0; i--) keys.push(shiftMonthKey(thisMonth, -i));
  }

  return keys.map((key) => ({
    key,
    label: labelOf(grain, key),
    caption: captionOf(grain, key),
    volumeKg: 0,
    bodyWeight: null,
    bodyFat: null,
    trainedDays: 0,
  }));
}

function labelOf(grain: Grain, key: string): string {
  if (grain === 'month') {
    const [, m] = key.split('-');
    return `${Number(m)}月`;
  }
  const d = parseKey(key);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function captionOf(grain: Grain, key: string): string {
  if (grain === 'month') {
    const [y, m] = key.split('-');
    return `${y}年${Number(m)}月`;
  }
  if (grain === 'week') {
    const end = shiftDays(key, 6);
    return `${labelOf('week', key)} 〜 ${labelOf('week', end)} の週`;
  }
  return key;
}

export function buildBuckets(grain: Grain, workouts: Workout[], to = today()): Bucket[] {
  const buckets = emptyBuckets(grain, to);
  const byKey = new Map(buckets.map((b) => [b.key, b]));

  // 体組成は平均なので、合計と件数を別に持つ
  const bodySum = new Map<string, { w: number; wN: number; f: number; fN: number }>();

  for (const workout of workouts) {
    const bucket = byKey.get(keyOf(grain, workout.date));
    if (!bucket) continue;

    bucket.volumeKg += volumeOf(workout.entries);
    if (workout.entries.length > 0) bucket.trainedDays += 1;

    const acc = bodySum.get(bucket.key) ?? { w: 0, wN: 0, f: 0, fN: 0 };
    if (workout.bodyWeight !== undefined) {
      acc.w += workout.bodyWeight;
      acc.wN += 1;
    }
    if (workout.bodyFat !== undefined) {
      acc.f += workout.bodyFat;
      acc.fN += 1;
    }
    bodySum.set(bucket.key, acc);
  }

  for (const bucket of buckets) {
    const acc = bodySum.get(bucket.key);
    if (!acc) continue;
    if (acc.wN > 0) bucket.bodyWeight = Math.round((acc.w / acc.wN) * 10) / 10;
    if (acc.fN > 0) bucket.bodyFat = Math.round((acc.f / acc.fN) * 10) / 10;
  }

  return buckets;
}

/** 期間内の最初と最後の実測値の差 (見出しに出す) */
export function changeOf(buckets: Bucket[], pick: (b: Bucket) => number | null) {
  const values = buckets.map(pick).filter((v): v is number => v !== null);
  if (values.length === 0) return null;
  const first = values[0]!;
  const last = values[values.length - 1]!;
  return { first, last, delta: Math.round((last - first) * 10) / 10, count: values.length };
}

export const dateKeyToday = toKey;
